import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSettings, FiLogOut, FiLock } from 'react-icons/fi';
import { useAppDispatch } from '../../store';
import { setBank, setPso, setUser } from '../../store/slices';
import { signOutUser } from '../../screens/login/service/auth';
import Icon from '../Icon';
import ChangePasswordModal from '../ChangePasswordModal';
import LogoutConfirmationModal from '../LogoutConfirmationModal';

export default function UserSettingsDropdown() {
    const [isOpen, setIsOpen] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleLogout = async () => {
        try {
            await signOutUser();
        } finally {
            dispatch(setPso(null));
            dispatch(setBank(null));
            dispatch(setUser(null));
            sessionStorage.removeItem('role');
            sessionStorage.removeItem('toast');
            navigate('/login');
        }
    };

    const handleChangePassword = () => {
        setIsOpen(false);
        setShowPasswordModal(true);
    };

    const handleLogoutClick = () => {
        setIsOpen(false);
        setShowLogoutModal(true);
    };

    return (
        <>
            <div className="relative" ref={dropdownRef}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="rounded-lg p-2 text-gray-600 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                    aria-label="User settings"
                >
                    <Icon icon={FiSettings} className="h-5 w-5" />
                </button>

                {isOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                        <div className="py-1">
                            <button
                                onClick={handleChangePassword}
                                className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                <Icon icon={FiLock} className="h-4 w-4" />
                                Change Password
                            </button>
                            <button
                                onClick={handleLogoutClick}
                                className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                            >
                                <Icon icon={FiLogOut} className="h-4 w-4" />
                                Logout
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <ChangePasswordModal
                isOpen={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
            />

            <LogoutConfirmationModal
                isOpen={showLogoutModal}
                onConfirm={() => {
                    setShowLogoutModal(false);
                    handleLogout();
                }}
                onCancel={() => setShowLogoutModal(false)}
            />
        </>
    );
}
